import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import TestimonialsSection from "@/components/TestimonialsSection";
import AstroBackground from "@/components/AstroBackground";
import ZodiacOrb from "@/components/ZodiacOrb";
import useScrollReveal from "@/hooks/useScrollReveal";
import useSEO from "@/hooks/useSEO";
import { WHATSAPP_URL, HERO_PHOTO, POLICIES } from "@/data/constants";

const GOLD = "#B89047";
const CHARCOAL = "#1A1A1A";
const OFF_WHITE = "#FDFCF8";
const BORDER = "#E5E0D8";

const BOOKS = [
  { num: "01", title: "The Science Of Vedic Astrology", desc: "An authoritative exploration of planetary influences, karmic cycles, and the cosmic architecture governing human destiny.", bg: "#0D1420" },
  { num: "02", title: "Nakshatras: Unlocking Your Destiny Through The 27 Lunar Mansions", desc: "A definitive guide to the 27 Nakshatras and their profound role in shaping character, timing, and the arc of a lifetime.", bg: "#0D1A10" },
  { num: "03", title: "Vastu: The House That Breathes", desc: "A transformative study of living spaces as energetic entities — aligning architecture with cosmic harmony and well-being.", bg: "#1C1208" },
  { num: "04", title: "The Blind Spot: Fault in Human Judgement", desc: "An incisive inquiry into cognitive bias and the role of planetary cycles in shaping — and distorting — human decision-making.", bg: "#14101E" },
];

const INDUSTRIES = [
  "Bureaucrats & Government Officials",
  "Real Estate & Infrastructure",
  "Finance, Investment & HNIs",
  "Manufacturing & Industrialists",
  "Political Leaders",
  "Celebrities & Entertainment",
  "Startup Founders & Tech Entrepreneurs",
  "Export-Import & Trading Businesses",
  "Healthcare Professionals",
];

const HP_SERVICES = [
  { id: "vedic-astrology", symbol: "☉", eyebrow: "Vedic Sciences", title: "Vedic Astrology & Kundli", desc: "Birth chart analysis, planetary periods, and transit readings — personalised guidance for career, relationships, and life decisions.", path: "/services/vedic-astrology" },
  { id: "numerology", symbol: "☿", eyebrow: "Name & Numbers", title: "Numerology", desc: "Decode the vibrational patterns in your name and date of birth for clarity on direction, purpose, and timing cycles.", path: "/services/numerology" },
  { id: "vastu-residential", symbol: "⊕", eyebrow: "Space & Energy", title: "Residential Vastu", desc: "Align your home's energy architecture with cosmic principles for health, prosperity, and harmony in daily life.", path: "/services/vastu" },
  { id: "vastu-commercial", symbol: "♃", eyebrow: "Space & Energy", title: "Commercial & Industrial Vastu", desc: "Optimise the energy of your office, commercial space, or industrial facility for measurable business growth.", path: "/services/vastu" },
  { id: "astrocartography", symbol: "♄", eyebrow: "Location Intelligence", title: "Astrocartography", desc: "Map your strongest planetary lines worldwide — for relocation, career growth, and international expansion.", path: "/services/astrocartography" },
];

const HOW_IT_WORKS_STEPS = [
  { num: "01", title: "Select & Message", desc: "Choose your service and send a WhatsApp message or use the Book & Pay button on the service page to begin." },
  { num: "02", title: "Pay in Advance", desc: "100% advance payment via UPI or bank transfer. Confirmation is sent once payment is verified." },
  { num: "03", title: "Personal Consultation", desc: "Your session is conducted personally by Akkshay Narula — no agents, no automated systems." },
];

function useStaggerReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const revealAll = () => {
      el.querySelectorAll(".card-hidden").forEach((c) => c.classList.add("card-visible"));
      el.querySelectorAll(".step-hidden").forEach((c) => c.classList.add("step-visible"));
    };
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) { revealAll(); return; }
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { revealAll(); obs.disconnect(); }
    }, { threshold: 0.02, rootMargin: "0px 0px 80px 0px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

function ClipReveal({ children, delay = 0 }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      const t = setTimeout(() => el.classList.add("revealed"), delay * 1000);
      return () => clearTimeout(t);
    }
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.classList.add("revealed"); obs.disconnect(); }
    }, { threshold: 0.01, rootMargin: "0px 0px 60px 0px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return <div ref={ref} className="clip-reveal" style={{ transitionDelay: `${delay}s` }}>{children}</div>;
}

function StatItem({ value, label }) {
  return (
    <div style={{ textAlign: "center" }}>
      <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.6rem, 3vw, 2.2rem)", fontWeight: 500, color: OFF_WHITE, lineHeight: 1.1 }}>{value}</p>
      <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.6rem", letterSpacing: "0.16em", textTransform: "uppercase", color: "#6A6A6A", marginTop: "6px" }}>{label}</p>
    </div>
  );
}

function ServiceTile({ service, index }) {
  return (
    <Link
      to={service.path}
      data-testid={`service-tile-${service.id}`}
      className={`card-hidden card-d${index + 1}`}
      style={{
        display: "block",
        position: "relative",
        padding: "40px 36px",
        border: `1px solid ${BORDER}`,
        backgroundColor: OFF_WHITE,
        textDecoration: "none",
        overflow: "hidden",
        transition: "border-color 0.3s, background-color 0.3s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.backgroundColor = "#F9F7F0"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.backgroundColor = OFF_WHITE; }}
    >
      <span style={{ position: "absolute", top: "-8px", right: "20px", fontFamily: "serif", fontSize: "5rem", color: GOLD, opacity: 0.07, lineHeight: 1, userSelect: "none", pointerEvents: "none" }}>
        {service.symbol}
      </span>
      <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.6rem", letterSpacing: "0.2em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "16px" }}>
        {service.eyebrow}
      </p>
      <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.45rem", fontWeight: 500, color: CHARCOAL, lineHeight: 1.3, marginBottom: "16px" }}>
        {service.title}
      </h3>
      <div className="shimmer-line" style={{ width: "32px", marginBottom: "16px" }} />
      <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.84rem", color: "#4A4A4A", lineHeight: 1.75, marginBottom: "28px" }}>
        {service.desc}
      </p>
      <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontFamily: "'Outfit', sans-serif", fontSize: "0.68rem", letterSpacing: "0.14em", textTransform: "uppercase", color: GOLD, fontWeight: 600 }}>
        Explore Service <ArrowRight size={11} />
      </span>
    </Link>
  );
}

export default function HomePage() {
  const aboutRef = useScrollReveal();
  useSEO({
    title: "Akkshay Narula | Vedic Astrologer, Numerologist & Vastu Consultant",
    description: "Akkshay Narula — celebrity astrologer, numerologist, Vastu consultant and author with 10+ years of practice. 24,908+ consultations across 120+ countries. Personal sessions in Guwahati, Bangalore and online worldwide.",
    keywords: "vedic astrologer India, best astrologer Guwahati, astrologer Bangalore, numerologist India, vastu consultant India, kundli analysis online, astrocartography India",
    canonical: "/",
  });
  const cardsRef = useStaggerReveal();
  const booksRef = useStaggerReveal();
  const howItWorksRef = useStaggerReveal();
  const policiesRef = useScrollReveal();
  const verifyRef = useScrollReveal();
  const ctaRef = useScrollReveal();

  return (
    <main style={{ backgroundColor: OFF_WHITE }}>

      {/* ── Hero ── */}
      <section data-testid="hero-section" style={{ padding: "80px 0 96px", position: "relative", overflow: "hidden" }}>
        <AstroBackground count={14} />
        <div style={{ position: "absolute", top: "-80px", right: "-80px", zIndex: 0, pointerEvents: "none" }}>
          <ZodiacOrb size={520} opacity={0.07} animate={true} />
        </div>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16" style={{ position: "relative", zIndex: 1 }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.6rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "20px" }}>
                Celebrity Astrologer · Author · Numerologist · Vastu Consultant
              </p>
              <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(3.5rem, 8vw, 6rem)", fontWeight: 400, color: CHARCOAL, lineHeight: 0.95, letterSpacing: "-0.02em", marginBottom: "8px" }}>
                Akkshay
              </h1>
              <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(3.5rem, 8vw, 6rem)", fontWeight: 400, color: CHARCOAL, lineHeight: 0.95, letterSpacing: "-0.02em", marginBottom: "28px" }}>
                Narula
              </h1>
              <div className="shimmer-line" style={{ width: "56px", marginBottom: "28px" }} />
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.82rem", color: "#6A6A6A", marginBottom: "8px", lineHeight: 1.6 }}>
                Vedic Astrology · Numerology · Vastu · Astrocartography
              </p>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.78rem", color: "#8A8A8A", marginBottom: "4px" }}>
                Author of 4 Books &nbsp;·&nbsp; 24,908+ Consultations Worldwide &nbsp;·&nbsp; 10+ Years
              </p>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.72rem", color: "#9A9A9A", marginBottom: "36px" }}>
                All consultations conducted personally. No agents. No automation.
              </p>
              <div style={{ display: "flex", gap: "14px", flexWrap: "wrap" }}>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-whatsapp-button"
                  style={{ backgroundColor: CHARCOAL, color: OFF_WHITE, padding: "14px 28px", fontFamily: "'Outfit', sans-serif", fontSize: "0.7rem", letterSpacing: "0.14em", textTransform: "uppercase", textDecoration: "none", fontWeight: 500, display: "inline-flex", alignItems: "center", gap: "8px", transition: "background-color 0.3s" }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = GOLD; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = CHARCOAL; }}
                >
                  Request Consultation <ArrowRight size={12} />
                </a>
                <Link
                  to="/consultations"
                  data-testid="hero-fees-link"
                  style={{ backgroundColor: "transparent", color: CHARCOAL, padding: "14px 28px", fontFamily: "'Outfit', sans-serif", fontSize: "0.7rem", letterSpacing: "0.14em", textTransform: "uppercase", textDecoration: "none", fontWeight: 500, border: `1px solid ${BORDER}`, display: "inline-flex", alignItems: "center", gap: "8px", transition: "border-color 0.3s" }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = GOLD; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = BORDER; }}
                >
                  View Fees & Pricing
                </Link>
              </div>
            </div>
            <div style={{ position: "relative" }}>
              <div style={{ border: `1px solid ${BORDER}`, overflow: "hidden", backgroundColor: "#F5F3EB" }}>
                <img
                  src={HERO_PHOTO}
                  alt="Akkshay Narula — Astrologer & Author"
                  style={{ width: "100%", height: "560px", objectFit: "cover", objectPosition: "center top", display: "block" }}
                />
              </div>
              <div style={{ position: "absolute", bottom: "-1px", left: "0", right: "0", padding: "20px 24px", backgroundColor: "rgba(26,26,26,0.92)", backdropFilter: "blur(8px)" }}>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.62rem", letterSpacing: "0.16em", textTransform: "uppercase", color: "#6A6A6A", marginBottom: "4px" }}>Currently Accepting</p>
                <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.05rem", fontWeight: 400, color: OFF_WHITE }}>Consultations by prior appointment only</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Authority Bar ── */}
      <section data-testid="authority-bar" style={{ backgroundColor: CHARCOAL, padding: "48px 0", position: "relative", overflow: "hidden" }}>
        <AstroBackground count={6} />
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16" style={{ position: "relative", zIndex: 1 }}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <StatItem value="24908+" label="Consultations Worldwide" />
            <StatItem value="120+" label="Countries Served" />
            <StatItem value="4" label="Books Published" />
            <StatItem value="Personal" label="Every Consultation" />
          </div>
        </div>
      </section>

      {/* ── About Akkshay ── */}
      <section
        ref={aboutRef}
        data-testid="about-section"
        className="sr-hidden"
        style={{ padding: "96px 0", backgroundColor: "#F5F3EB", borderBottom: `1px solid ${BORDER}` }}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left: Bio */}
            <div>
              <ClipReveal>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.62rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "14px" }}>
                  About the Practitioner
                </p>
                <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 500, color: CHARCOAL, lineHeight: 1.1, marginBottom: "28px" }}>
                  A Decade of Precise,<br />Personal Guidance
                </h2>
              </ClipReveal>
              <div className="shimmer-line" style={{ width: "48px", marginBottom: "28px" }} />
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.9rem", color: "#4A4A4A", lineHeight: 1.85, marginBottom: "18px" }}>
                With over <strong style={{ color: CHARCOAL, fontWeight: 600 }}>10 years of full-time practice</strong>, Akkshay Narula has built one of India's most trusted practices in Vedic astrology, numerology, Vastu, and astrocartography. He has personally guided 24,908+ clients across 120+ countries — conducting every consultation himself, without agents or intermediaries.
              </p>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.9rem", color: "#4A4A4A", lineHeight: 1.85, marginBottom: "32px" }}>
                Author of four published books on Vedic astrology, numerology, and Vastu, his practice combines rigorous scholarship with practical, high-stakes guidance for individuals and organisations navigating complex life and business decisions.
              </p>
              <div style={{ display: "flex", gap: "40px", borderTop: `1px solid ${BORDER}`, paddingTop: "28px" }}>
                <div>
                  <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "2.2rem", fontWeight: 500, color: CHARCOAL, lineHeight: 1 }}>10+</p>
                  <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.6rem", letterSpacing: "0.16em", textTransform: "uppercase", color: "#8A8A8A", marginTop: "5px" }}>Years Practice</p>
                </div>
                <div>
                  <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "2.2rem", fontWeight: 500, color: CHARCOAL, lineHeight: 1 }}>2014</p>
                  <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.6rem", letterSpacing: "0.16em", textTransform: "uppercase", color: "#8A8A8A", marginTop: "5px" }}>Practice Since</p>
                </div>
              </div>
            </div>
            {/* Right: Industries */}
            <div>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.62rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "24px" }}>
                Industries Served
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
                {INDUSTRIES.map((ind, i) => (
                  <div
                    key={i}
                    style={{ display: "flex", alignItems: "center", gap: "16px", padding: "14px 0", borderBottom: `1px solid ${BORDER}` }}
                  >
                    <span style={{ color: GOLD, fontFamily: "serif", fontSize: "0.8rem", flexShrink: 0, lineHeight: 1 }}>—</span>
                    <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.86rem", color: "#3A3A3A", lineHeight: 1.5 }}>{ind}</p>
                  </div>
                ))}
              </div>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.78rem", color: "#8A8A8A", lineHeight: 1.7, marginTop: "24px", fontStyle: "italic" }}>
                Across all segments — from government officials to startup founders — clients seek Akkshay Narula for precision guidance on high-stakes decisions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Services Overview ── */}
      <section data-testid="services-section" style={{ padding: "96px 0", backgroundColor: OFF_WHITE }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <ClipReveal>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.62rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "12px" }}>What I Offer</p>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 500, color: CHARCOAL, marginBottom: "14px" }}>Services</h2>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.88rem", color: "#6A6A6A", lineHeight: 1.7, maxWidth: "520px", marginBottom: "56px" }}>
              All consultations conducted personally. Each session is tailored to your chart, your concern, and your context.
            </p>
          </ClipReveal>
          <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {HP_SERVICES.slice(0, 4).map((s, i) => (
              <ServiceTile key={s.id} service={s} index={i} />
            ))}
            <div className="md:col-span-2">
              <ServiceTile service={HP_SERVICES[4]} index={4} />
            </div>
          </div>
          <div style={{ textAlign: "center", marginTop: "40px" }}>
            <Link
              to="/consultations"
              style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.68rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "#6A6A6A", textDecoration: "none", borderBottom: `1px solid ${BORDER}`, paddingBottom: "2px", transition: "color 0.2s, border-color 0.2s" }}
              onMouseEnter={(e) => { e.currentTarget.style.color = GOLD; e.currentTarget.style.borderColor = GOLD; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = "#6A6A6A"; e.currentTarget.style.borderColor = BORDER; }}
            >
              View All Fees & Pricing →
            </Link>
          </div>
        </div>
      </section>

      {/* ── Published Books ── */}
      <section data-testid="books-section" style={{ padding: "96px 0", backgroundColor: CHARCOAL, position: "relative", overflow: "hidden" }}>
        <AstroBackground count={16} />
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16" style={{ position: "relative", zIndex: 1 }}>
          <ClipReveal>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.62rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "12px" }}>Published Works</p>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 500, color: OFF_WHITE, marginBottom: "56px" }}>Books by Akkshay Narula</h2>
          </ClipReveal>
          <div ref={booksRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px" style={{ backgroundColor: "#222222" }}>
            {BOOKS.map((book, i) => (
              <div
                key={book.num}
                data-testid={`book-card-${i + 1}`}
                className={`card-hidden card-d${i + 1}`}
                style={{ backgroundColor: book.bg, padding: "40px 32px", display: "flex", flexDirection: "column", minHeight: "300px" }}
              >
                <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "2.2rem", fontWeight: 400, color: "#2A2A3A", lineHeight: 1 }}>{book.num}</p>
                <div className="shimmer-line" style={{ width: "28px", margin: "20px 0" }} />
                <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.12rem", fontWeight: 500, color: OFF_WHITE, lineHeight: 1.45, flex: 1 }}>{book.title}</h3>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.78rem", color: "#6A6A6A", lineHeight: 1.75, marginTop: "16px" }}>{book.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <TestimonialsSection />

      {/* ── How It Works ── */}
      <section data-testid="how-it-works-section" style={{ padding: "96px 0", backgroundColor: OFF_WHITE }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <ClipReveal>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.62rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "12px" }}>Simple Process</p>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 500, color: CHARCOAL, marginBottom: "56px" }}>How It Works</h2>
          </ClipReveal>
          <div ref={howItWorksRef} className="grid grid-cols-1 md:grid-cols-3 gap-0">
            {HOW_IT_WORKS_STEPS.map((step, i) => (
              <div
                key={i}
                data-testid={`how-it-works-step-${i + 1}`}
                className={`step-hidden step-d${i + 1}`}
                style={{ padding: "40px 36px", borderRight: i < 2 ? `1px solid ${BORDER}` : "none", borderBottom: `1px solid ${BORDER}` }}
              >
                <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "2.5rem", fontWeight: 400, color: BORDER, lineHeight: 1, marginBottom: "20px" }}>{step.num}</p>
                <div className="shimmer-line" style={{ width: "28px", marginBottom: "20px" }} />
                <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.3rem", fontWeight: 500, color: CHARCOAL, marginBottom: "12px" }}>{step.title}</h3>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.86rem", color: "#4A4A4A", lineHeight: 1.75 }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Policies ── */}
      <section
        ref={policiesRef}
        data-testid="policies-section"
        className="sr-hidden"
        style={{ padding: "80px 0", backgroundColor: "#F5F3EB", borderTop: `1px solid ${BORDER}` }}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.62rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "12px" }}>Important</p>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)", fontWeight: 500, color: CHARCOAL, marginBottom: "40px" }}>Booking Policies</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {POLICIES.map((policy, i) => (
              <div key={i} style={{ padding: "28px", backgroundColor: OFF_WHITE, border: `1px solid ${BORDER}` }}>
                <div style={{ width: "4px", height: "28px", backgroundColor: GOLD, marginBottom: "16px" }} />
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.86rem", color: "#3A3A3A", lineHeight: 1.75 }}>{policy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Verify Banner ── */}
      <section
        ref={verifyRef}
        data-testid="home-verify-banner"
        className="sr-hidden"
        style={{ backgroundColor: CHARCOAL, padding: "64px 0", position: "relative", overflow: "hidden" }}
      >
        <AstroBackground count={8} />
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16" style={{ position: "relative", zIndex: 1 }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.62rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "14px" }}>Anti-Fraud Protection</p>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)", fontWeight: 500, color: OFF_WHITE, marginBottom: "16px" }}>
                Verify Authenticity Before You Pay
              </h2>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.88rem", color: "#8A8A8A", lineHeight: 1.75 }}>
                Fraud in the name of astrologers is common. Verify that you are interacting with the official domain and genuine contact number before making any payment.
              </p>
            </div>
            <div style={{ display: "flex", gap: "14px", flexWrap: "wrap", justifyContent: "flex-start" }}>
              <Link
                to="/verify"
                data-testid="verify-cta-btn"
                style={{ backgroundColor: GOLD, color: CHARCOAL, padding: "14px 28px", fontFamily: "'Outfit', sans-serif", fontSize: "0.7rem", letterSpacing: "0.14em", textTransform: "uppercase", textDecoration: "none", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: "8px" }}
              >
                Verify Now <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section
        ref={ctaRef}
        data-testid="final-cta-section"
        className="sr-hidden"
        style={{ padding: "96px 0", textAlign: "center" }}
      >
        <div className="max-w-xl mx-auto px-6">
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.62rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "16px" }}>Ready to Begin</p>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2.4rem, 5vw, 3.6rem)", fontWeight: 400, color: CHARCOAL, lineHeight: 1.1, marginBottom: "20px" }}>
            Every Answer Starts<br />with a Single Conversation
          </h2>
          <div className="shimmer-line" style={{ width: "48px", margin: "0 auto 24px" }} />
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.88rem", color: "#6A6A6A", marginBottom: "36px", lineHeight: 1.75 }}>
            Message on WhatsApp to discuss your requirements and schedule your consultation.
          </p>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="final-cta-whatsapp"
            style={{ backgroundColor: CHARCOAL, color: OFF_WHITE, padding: "16px 36px", fontFamily: "'Outfit', sans-serif", fontSize: "0.72rem", letterSpacing: "0.14em", textTransform: "uppercase", textDecoration: "none", fontWeight: 500, display: "inline-flex", alignItems: "center", gap: "10px", transition: "background-color 0.3s" }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = GOLD; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = CHARCOAL; }}
          >
            Request Consultation on WhatsApp <ArrowRight size={13} />
          </a>
        </div>
      </section>

    </main>
  );
}
